import { Box, type Breakpoint, CircularProgress, Typography } from "@mui/material";
import type { CSSProperties, FC } from "react";
import { useTranslation } from "react-i18next";
import { ShiftPeriodIcon } from "../../components/UIToolkit/Icons/ShiftPeriodIcon";
import { SystemDropdown } from "../../components/UIToolkit/Inputs/SystemDropDown";
import { isShiftClockTime, shiftPeriodOf } from "../../utils/shiftPeriod";
import { SHIFT_TYPE_COLORS } from "./DepartmentAlerts/utils";
import { formatShiftTime, type ShiftHourOption, toShiftHourKey } from "./useShiftHourScope";

type Props = {
	shiftHourOptions: ShiftHourOption[];
	selectedShiftHours: string[];
	onShiftHoursChange: (values: string[]) => void;
	updating: boolean;
	/** Options never loaded, so reports run without a Shift Hours filter. */
	unavailable: boolean;
	width?: CSSProperties["width"] | Partial<Record<Breakpoint, CSSProperties["width"]>>;
};

export const ShiftHoursSelect: FC<Props> = ({ shiftHourOptions, selectedShiftHours, onShiftHoursChange, updating, unavailable, width }) => {
	const { t } = useTranslation();

	return (
		<Box sx={{ position: "relative" }}>
			<SystemDropdown
				label={t("shift.hours")}
				data-testid="management-center-shift-hours-select"
				disabled={updating || unavailable || shiftHourOptions.length === 0}
				loading={updating}
				multiple
				items={shiftHourOptions.map((opt) => {
					const key = toShiftHourKey(opt);
					// DEFAULT: shiftPeriodOf throws on an unparsable start time; this filter renders live
					// shift_template data and must not crash on a bad row, so a time outside the clock grammar
					// shows as Night (what the old hour-prefix rule gave any time without a leading hour)
					// instead of throwing during render.
					const category = isShiftClockTime(opt.start_time) ? shiftPeriodOf(opt.start_time) : "Night";
					const colors = SHIFT_TYPE_COLORS[category];
					return {
						label: `${formatShiftTime(opt.start_time)}-${formatShiftTime(opt.end_time)}`,
						value: key,
						icon: <ShiftPeriodIcon period={category} sx={{ fontSize: 16, color: colors.text, mr: 0.5 }} />,
					};
				})}
				selectedItems={selectedShiftHours}
				onChangeMultiple={(values) => onShiftHoursChange(values)}
				selectSx={{
					width: width ?? "200px",
				}}
				formControlSx={{
					width: width ?? "300px",
					minWidth: width ?? 0,
				}}
				renderAllValueText={t("All")}
			/>
			{updating && (
				<Box
					role="status"
					aria-label={t("managementCenter.shiftHoursUpdating")}
					sx={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
				>
					<CircularProgress size={14} />
				</Box>
			)}
			{unavailable && (
				<Typography variant="caption" color="error">
					{t("managementCenter.shiftHoursUnavailable")}
				</Typography>
			)}
		</Box>
	);
};
